import { OrderHistory } from '../stores/appOrderHistoryStore';
import { OrderFilters } from '../hooks/useOrderFilters';
import { RevenueFilters } from '../hooks/useRevenueFilters';
import { PriceAlertFilters } from '../hooks/usePriceAlertFilters';
import {
  filterOrdersByPlatforms,
  filterOrdersBySpecificCategories,
  filterOrdersBySubCategories,
  filterOrdersByRestaurants,
  filterOrdersByRestaurantSections,
} from '../components/orderhistory/utils';
import {
  filterRevenueByCategories,
  filterRevenueByMenuCategories,
  filterRevenueByRestaurants,
  filterRevenueBySources,
} from '../components/revenuehistory/utils';
import {
  filterPriceAlertsByPlatforms,
  filterPriceAlertsBySections,
  filterPriceAlertsByCategories,
  filterPriceAlertsByRestaurants,
  filterPriceAlertsByChangeType,
} from './priceAlertUtils';
import { PriceAlert } from '../services/priceAlerts.ts/priceAlertsService';

/**
 * Apply all order filters in a consistent sequence
 * Filters are applied in order: restaurants -> sections -> platforms -> specific categories -> subcategories
 *
 * @param orders - Array of orders to filter
 * @param filters - Filter configuration from useOrderFilters hook
 * @param restaurantFilters - Optional restaurant filters for "All Restaurants" view
 * @param platformToSectionIndex - Optional index to map platform IDs to sections for restaurant filtering
 * @param subCategoryFilters - Optional subcategory filters
 * @returns Filtered array of orders
 */
export function filterAllOrders(
  orders: OrderHistory[],
  filters: OrderFilters,
  restaurantFilters?: string[],
  platformToSectionIndex?: Record<string, any>,
  subCategoryFilters?: string[]
): OrderHistory[] {
  let filtered = orders;

  // Apply restaurant filters (for "All Restaurants" view)
  if (restaurantFilters && restaurantFilters.length > 0) {
    filtered = filterOrdersByRestaurants(filtered, restaurantFilters, platformToSectionIndex);
  }

  // Apply section filters (ordering accounts)
  filtered = filterOrdersByRestaurantSections(filtered, filters.sections);

  // Apply platform filters
  filtered = filterOrdersByPlatforms(filtered, filters.platforms);

  // Apply specific category filters
  filtered = filterOrdersBySpecificCategories(filtered, filters.categories);

  // Apply subcategory filters
  if (subCategoryFilters && subCategoryFilters.length > 0) {
    filtered = filterOrdersBySubCategories(filtered, subCategoryFilters);
  }

  return filtered;
}

/**
 * Apply all revenue filters in a consistent sequence
 * Filters are applied in order: restaurants -> revenue sources -> revenue categories -> menu categories
 *
 * @param revenue - Array of revenue records to filter
 * @param filters - Filter configuration from useRevenueFilters hook
 * @param costPriceAnalyses - Cost price analyses for menu category mapping
 * @param categories - Available menu categories
 * @param restaurantFilters - Optional restaurant filters for "All Restaurants" view
 * @param revenueSources - The restaurant's revenue source rows; required to resolve the
 *                         source-name filter to source ids (omit = source filter is a no-op)
 * @returns Filtered array of revenue records
 */
export function filterAllRevenue(
  revenue: any[],
  filters: RevenueFilters,
  costPriceAnalyses?: any[],
  categories?: any[],
  restaurantFilters?: string[],
  revenueSources?: any[]
): any[] {
  let filtered = revenue;

  // Apply restaurant filters (for "All Restaurants" view). Empty = no filter = all restaurants.
  if (restaurantFilters && restaurantFilters.length > 0) {
    filtered = filterRevenueByRestaurants(filtered, restaurantFilters);
  }

  // Apply revenue source filters (venue: the restaurant itself vs a partner like bar POP).
  // Needs the source rows to resolve names to ids, so it no-ops without them.
  if (revenueSources && revenueSources.length > 0) {
    filtered = filterRevenueBySources(filtered, filters.revenueSources, revenueSources);
  }

  // Apply revenue category filters (Food, Drinks, etc.)
  filtered = filterRevenueByCategories(filtered, filters.revenueCategories);

  // Apply menu category filters (only if we have the required data)
  if (costPriceAnalyses !== undefined && categories !== undefined) {
    filtered = filterRevenueByMenuCategories(filtered, filters.menuCategories, costPriceAnalyses, categories);
  }

  return filtered;
}

/**
 * Apply all price alert filters in a consistent sequence
 * Filters are applied in order: restaurants -> sections -> platforms -> categories
 *
 * @param alerts - Array of price alerts to filter
 * @param filters - Filter configuration from usePriceAlertFilters hook
 * @param restaurantFilters - Optional restaurant filters for "All Restaurants" view
 * @param platformToSectionIndex - Optional index to map platform IDs to sections for restaurant and section filtering
 * @returns Filtered array of price alerts
 */
export function filterAllPriceAlerts(
  alerts: PriceAlert[],
  filters: PriceAlertFilters,
  restaurantFilters?: string[],
  platformToSectionIndex?: Record<string, any>
): PriceAlert[] {
  let filtered = alerts;

  // Apply restaurant filters (for "All Restaurants" view)
  if (restaurantFilters && restaurantFilters.length > 0) {
    filtered = filterPriceAlertsByRestaurants(filtered, restaurantFilters, platformToSectionIndex);
  }

  // Apply section filters (ordering accounts)
  filtered = filterPriceAlertsBySections(filtered, filters.sections, platformToSectionIndex);

  // Apply platform filters
  filtered = filterPriceAlertsByPlatforms(filtered, filters.platforms);

  // Apply category filters
  filtered = filterPriceAlertsByCategories(filtered, filters.categories);

  // Apply change type filter (all/increases/decreases)
  filtered = filterPriceAlertsByChangeType(filtered, filters.changeType);

  return filtered;
}
